import { useState, useEffect, useCallback, useRef } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Link2, Loader2, Check, RefreshCw, TrendingUp, Plus, Pause, Play, Trash2,
  ChevronLeft, Layers, Megaphone, Info, Users, Search, X, Sparkles, FileText, Pencil, Eye,
  Upload, Image as ImageIcon, Video as VideoIcon, Images,
  Heart, MessageCircle, Send, Bookmark, Volume2, MoreHorizontal, Target, Copy,
} from 'lucide-react';

const STATUS_LABELS = {
  ACTIVE: 'Activa', PAUSED: 'Pausada', DELETED: 'Eliminada', ARCHIVED: 'Archivada',
};

const OBJECTIVE_LABELS = {
  OUTCOME_TRAFFIC: 'Tráfico — llevar gente a un enlace (tu web, WhatsApp, etc.)',
  OUTCOME_ENGAGEMENT: 'Interacción — más likes, comentarios y compartidos en tu página',
  OUTCOME_LEADS: 'Leads — la gente llena un formulario de contacto sin salir de Facebook/Instagram',
};

const CTA_LABELS = {
  LEARN_MORE: 'Más información', WHATSAPP_MESSAGE: 'Enviar WhatsApp',
  MESSAGE_PAGE: 'Enviar mensaje', CALL_NOW: 'Llamar ahora', SHOP_NOW: 'Comprar ahora',
};

function money(cents) {
  return cents ? `$${(cents / 100).toFixed(2)}` : '—';
}

function StatusBadge({ status }) {
  return (
    <Badge className={status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-600' : 'bg-[#F1F5F9] text-[#64748B]'}>
      {STATUS_LABELS[status] || status}
    </Badge>
  );
}

export default function MetaAdsConfig() {
  const [status, setStatus] = useState('loading'); // loading | need_account | pick_account | campaigns | error
  const [accounts, setAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [adAccountName, setAdAccountName] = useState('');
  const [connecting, setConnecting] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [busyId, setBusyId] = useState(null);

  // Drill-down state: null = campaign list; otherwise a campaign/adset context.
  const [openCampaign, setOpenCampaign] = useState(null); // {id, name}
  const [adSets, setAdSets] = useState([]);
  const [openAdSet, setOpenAdSet] = useState(null); // {id, name}
  const [ads, setAds] = useState([]);

  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [audiencesModalOpen, setAudiencesModalOpen] = useState(false);
  const [pixelModalOpen, setPixelModalOpen] = useState(false);
  const [adSetModalOpen, setAdSetModalOpen] = useState(false);
  const [adModalOpen, setAdModalOpen] = useState(false);
  const [editingAd, setEditingAd] = useState(null); // {id} of the ad being edited, or null when creating
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [editingAdSetId, setEditingAdSetId] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadCampaigns = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/meta-ads/campaigns');
      setCampaigns(data.campaigns || []);
      setAdAccountName(data.ad_account_name || '');
      setStatus('campaigns');
    } catch (e) {
      if (e.response?.data?.detail?.code === 'ad_account_not_connected') {
        await loadAccounts();
      } else {
        setStatus('error');
        toast.error(formatApiError(e.response?.data?.detail));
      }
    }
  }, []);

  const loadAccounts = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/meta-ads/accounts');
      setAccounts(data.accounts || []);
      setStatus('pick_account');
    } catch (e) {
      if (e.response?.data?.detail?.code === 'meta_not_connected') {
        setStatus('need_account');
      } else {
        setStatus('error');
        toast.error(formatApiError(e.response?.data?.detail));
      }
    }
  }, []);

  const init = useCallback(async () => {
    setStatus('loading');
    await loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => { init(); }, [init]);

  const connectAccount = async () => {
    setRedirecting(true);
    try {
      const { data } = await api.get('/tenant/oauth/meta/authorize', {
        params: { return_to: window.location.pathname },
      });
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo iniciar la conexión con Meta');
      setRedirecting(false);
    }
  };

  const disconnectAdAccount = async () => {
    if (!window.confirm('Desconectar la cuenta publicitaria de este negocio?')) return;
    try {
      await api.post('/tenant/meta-ads/accounts/disconnect');
      toast.success('Cuenta publicitaria desconectada');
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail?.detail || e.response?.data?.detail || 'No se pudo desconectar');
    }
  };

  const connectAdAccount = async (account) => {
    setConnecting(account.id);
    try {
      await api.post(`/tenant/meta-ads/accounts/${account.id}/connect`, { account_name: account.name });
      toast.success(`Cuenta "${account.name}" conectada`);
      await loadCampaigns();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setConnecting(null);
    }
  };

  const loadAdSets = useCallback(async (campaign) => {
    setOpenCampaign(campaign);
    setOpenAdSet(null);
    try {
      const { data } = await api.get(`/tenant/meta-ads/campaigns/${campaign.id}/adsets`);
      setAdSets(data.adsets || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);

  const loadAds = useCallback(async (adset) => {
    setOpenAdSet(adset);
    try {
      const { data } = await api.get(`/tenant/meta-ads/adsets/${adset.id}/ads`);
      setAds(data.ads || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);

  const toggleCampaignStatus = async (c) => {
    setBusyId(c.id);
    try {
      const next = c.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
      await api.patch(`/tenant/meta-ads/campaigns/${c.id}`, { status: next });
      toast.success(next === 'ACTIVE' ? 'Campaña activada' : 'Campaña pausada');
      await loadCampaigns();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const deleteCampaign = async (c) => {
    if (!window.confirm(`¿Eliminar la campaña "${c.name}"? Esto la borra también en Meta Ads Manager.`)) return;
    setBusyId(c.id);
    try {
      await api.delete(`/tenant/meta-ads/campaigns/${c.id}`);
      toast.success('Campaña eliminada');
      await loadCampaigns();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const toggleAdSetStatus = async (a) => {
    setBusyId(a.id);
    try {
      const next = a.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
      await api.patch(`/tenant/meta-ads/adsets/${a.id}`, { status: next });
      await loadAdSets(openCampaign);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const deleteAdSet = async (a) => {
    if (!window.confirm(`¿Eliminar el conjunto de anuncios "${a.name}"?`)) return;
    setBusyId(a.id);
    try {
      await api.delete(`/tenant/meta-ads/adsets/${a.id}`);
      toast.success('Conjunto eliminado');
      await loadAdSets(openCampaign);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const toggleAdStatus = async (ad) => {
    setBusyId(ad.id);
    try {
      const next = ad.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
      await api.patch(`/tenant/meta-ads/ads/${ad.id}`, { status: next });
      await loadAds(openAdSet);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const deleteAd = async (ad) => {
    if (!window.confirm(`¿Eliminar el anuncio "${ad.name}"?`)) return;
    setBusyId(ad.id);
    try {
      await api.delete(`/tenant/meta-ads/ads/${ad.id}`);
      toast.success('Anuncio eliminado');
      await loadAds(openAdSet);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  if (status === 'loading') {
    return <div className="flex items-center gap-2 text-sm text-[#64748B] py-8"><Loader2 className="w-4 h-4 animate-spin" /> Cargando...</div>;
  }

  if (status === 'need_account') {
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
        <p className="text-sm text-[#64748B] mb-3">Conecta tu cuenta de Meta para ver y elegir tu cuenta publicitaria.</p>
        <Button onClick={connectAccount} disabled={redirecting} className="gap-2">
          {redirecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
          Conectar cuenta de Meta
        </Button>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
        <Button onClick={init} variant="outline" size="sm" className="gap-2"><RefreshCw className="w-4 h-4" /> Reintentar</Button>
      </div>
    );
  }

  if (status === 'pick_account') {
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
        <p className="text-xs text-[#64748B] mb-3">Elige la cuenta publicitaria que quieres ver aquí:</p>
        {accounts.length === 0 ? (
          <p className="text-sm text-[#94A3B8]">No encontramos cuentas publicitarias en tu cuenta de Meta.</p>
        ) : (
          <div className="space-y-2">
            {accounts.map(a => (
              <div key={a.id} className="flex items-center justify-between border border-[#E2E8F0] rounded-lg px-3 py-2">
                <div>
                  <p className="text-sm font-medium text-[#0F172A]">{a.name}</p>
                  <p className="text-[11px] text-[#94A3B8]">{a.currency}</p>
                </div>
                {a.connected ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-emerald-600 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Conectada</span>
                    <Button size="sm" variant="ghost" className="text-red-600 h-8" onClick={disconnectAdAccount}>Desconectar</Button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => connectAdAccount(a)} disabled={connecting === a.id}>
                    {connecting === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Conectar'}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // --- Ad drill-down (deepest level) ---
  if (openAdSet) {
    return (
      <div className="space-y-4">
        <button onClick={() => setOpenAdSet(null)} className="flex items-center gap-1 text-xs text-[#64748B] hover:text-[#0F172A]">
          <ChevronLeft className="w-3.5 h-3.5" /> {openCampaign?.name}
        </button>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2"><Megaphone className="w-4 h-4" /> {openAdSet.name}</h3>
          <Button size="sm" onClick={() => { setEditingAd(null); setAdModalOpen(true); }} className="gap-1"><Plus className="w-3.5 h-3.5" /> Nuevo anuncio</Button>
        </div>
        {ads.length === 0 ? (
          <p className="text-sm text-[#94A3B8] text-center py-6">Sin anuncios en este conjunto todavía</p>
        ) : (
          <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] text-xs text-[#94A3B8]">
                <tr><th className="text-left px-4 py-2">Anuncio</th><th className="text-left px-4 py-2">Estado</th><th className="text-right px-4 py-2">Acciones</th></tr>
              </thead>
              <tbody>
                {ads.map(ad => (
                  <tr key={ad.id} className="border-t border-[#F1F5F9] hover:bg-[#F8FAFC]">
                    <td className="px-4 py-2 text-[#0F172A] cursor-pointer" onClick={() => { setEditingAd({ id: ad.id }); setAdModalOpen(true); }}>{ad.name}</td>
                    <td className="px-4 py-2"><StatusBadge status={ad.status} /></td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" className="w-7 h-7" title="Editar contenido del anuncio"
                          onClick={() => { setEditingAd({ id: ad.id }); setAdModalOpen(true); }}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="w-7 h-7" disabled={busyId === ad.id} onClick={() => toggleAdStatus(ad)}
                          title={ad.status === 'ACTIVE' ? 'Pausar' : 'Activar'}>
                          {ad.status === 'ACTIVE' ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                        </Button>
                        <Button size="icon" variant="ghost" className="w-7 h-7 text-red-500" disabled={busyId === ad.id} onClick={() => deleteAd(ad)} title="Eliminar">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <AdModal
          open={adModalOpen} onClose={() => { setAdModalOpen(false); setEditingAd(null); }} saving={saving} setSaving={setSaving}
          isLeadAdSet={openAdSet.optimization_goal === 'LEAD_GENERATION'}
          isWhatsAppAdSet={openAdSet.destination_type === 'WHATSAPP'}
          editingAdId={editingAd?.id || null}
          onCreate={async (payload) => {
            await api.post(`/tenant/meta-ads/adsets/${openAdSet.id}/ads`, payload);
            toast.success('Anuncio creado (pausado, revísalo antes de activarlo)');
            setAdModalOpen(false);
            await loadAds(openAdSet);
          }}
          onSave={async (adId, payload) => {
            await api.patch(`/tenant/meta-ads/ads/${adId}`, payload);
            toast.success('Anuncio actualizado');
            setAdModalOpen(false);
            setEditingAd(null);
            await loadAds(openAdSet);
          }}
        />
      </div>
    );
  }

  // --- Ad Set drill-down ---
  if (openCampaign) {
    return (
      <div className="space-y-4">
        <button onClick={() => setOpenCampaign(null)} className="flex items-center gap-1 text-xs text-[#64748B] hover:text-[#0F172A]">
          <ChevronLeft className="w-3.5 h-3.5" /> Campañas
        </button>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2"><Layers className="w-4 h-4" /> {openCampaign.name}</h3>
          <Button size="sm" onClick={() => setAdSetModalOpen(true)} className="gap-1"><Plus className="w-3.5 h-3.5" /> Nuevo conjunto</Button>
        </div>
        {adSets.length === 0 ? (
          <p className="text-sm text-[#94A3B8] text-center py-6">Sin conjuntos de anuncios en esta campaña todavía</p>
        ) : (
          <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] text-xs text-[#94A3B8]">
                <tr>
                  <th className="text-left px-4 py-2">Conjunto</th><th className="text-left px-4 py-2">Presupuesto</th>
                  <th className="text-left px-4 py-2">Calendario</th>
                  <th className="text-left px-4 py-2">Estado</th><th className="text-right px-4 py-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {adSets.map(a => (
                  <tr key={a.id} className="border-t border-[#F1F5F9] hover:bg-[#F8FAFC]">
                    <td className="px-4 py-2 text-[#0F172A] cursor-pointer" onClick={() => loadAds(a)}>
                      {a.name}
                      {a.destination_type === 'WHATSAPP' && (
                        <Badge className="ml-2 bg-emerald-50 text-emerald-600 text-[10px]">WhatsApp</Badge>
                      )}
                    </td>
                    <td className="px-4 py-2 text-[#64748B]">
                      {a.daily_budget ? `${money(a.daily_budget)}/día` : a.lifetime_budget ? `${money(a.lifetime_budget)} total` : '—'}
                    </td>
                    <td className="px-4 py-2 text-[#64748B] text-xs">
                      {a.end_time ? `hasta ${new Date(a.end_time).toLocaleDateString('es-MX')}` : 'sin fecha de fin'}
                    </td>
                    <td className="px-4 py-2"><StatusBadge status={a.status} /></td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => loadAds(a)}>Ver anuncios</Button>
                        <Button size="icon" variant="ghost" className="w-7 h-7" title="Editar conjunto"
                          onClick={() => { setEditingAdSetId(a.id); setAdSetModalOpen(true); }}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="w-7 h-7" disabled={busyId === a.id} onClick={() => toggleAdSetStatus(a)}
                          title={a.status === 'ACTIVE' ? 'Pausar' : 'Activar'}>
                          {a.status === 'ACTIVE' ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                        </Button>
                        <Button size="icon" variant="ghost" className="w-7 h-7 text-red-500" disabled={busyId === a.id} onClick={() => deleteAdSet(a)} title="Eliminar">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <AdSetModal
          open={adSetModalOpen} onClose={() => { setAdSetModalOpen(false); setEditingAdSetId(null); }} saving={saving} setSaving={setSaving}
          campaignObjective={openCampaign.objective}
          editingAdSetId={editingAdSetId}
          onCreate={async (payload) => {
            await api.post(`/tenant/meta-ads/campaigns/${openCampaign.id}/adsets`, payload);
            toast.success('Conjunto de anuncios creado (pausado, revísalo antes de activarlo)');
            setAdSetModalOpen(false);
            await loadAdSets(openCampaign);
          }}
          onSave={async (adsetId, payload) => {
            await api.patch(`/tenant/meta-ads/adsets/${adsetId}`, payload);
            toast.success('Conjunto de anuncios actualizado');
            setAdSetModalOpen(false);
            setEditingAdSetId(null);
            await loadAdSets(openCampaign);
          }}
        />
      </div>
    );
  }

  // --- Campaign list (top level) ---
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2"><TrendingUp className="w-4 h-4" /> {adAccountName}</h3>
          <p className="text-xs text-[#64748B]">Crea y administra tus campañas de Meta Ads sin salir de CavernicolApp.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setCampaignModalOpen(true)} className="gap-1"><Plus className="w-3.5 h-3.5" /> Nueva campaña</Button>
          <Button size="sm" variant="outline" onClick={() => setAudiencesModalOpen(true)} className="gap-1"><Users className="w-3.5 h-3.5" /> Públicos</Button>
          <Button size="sm" variant="outline" onClick={() => setPixelModalOpen(true)} className="gap-1"><Target className="w-3.5 h-3.5" /> Píxel</Button>
          <Button size="sm" variant="outline" onClick={loadCampaigns} className="gap-1"><RefreshCw className="w-3.5 h-3.5" /> Actualizar</Button>
          <Button size="sm" variant="outline" onClick={loadAccounts}>Cambiar cuenta</Button>
        </div>
      </div>
      <p className="text-xs text-[#64748B] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        Toda campaña nueva se crea <strong>pausada</strong> por seguridad — revísala aquí o en Meta Ads Manager y actívala cuando estés listo. Cada campaña genera automáticamente su propia fuente de leads para tus reportes.
      </p>
      {campaigns.length === 0 ? (
        <p className="text-sm text-[#94A3B8] text-center py-6">Sin campañas en esta cuenta — crea la primera arriba</p>
      ) : (
        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] text-xs text-[#94A3B8]">
              <tr>
                <th className="text-left px-4 py-2">Campaña</th>
                <th className="text-left px-4 py-2">Objetivo</th>
                <th className="text-left px-4 py-2">Estado</th>
                <th className="text-left px-4 py-2">Presupuesto</th>
                <th className="text-right px-4 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map(c => (
                <tr key={c.id} className="border-t border-[#F1F5F9] hover:bg-[#F8FAFC]">
                  <td className="px-4 py-2 text-[#0F172A] cursor-pointer" onClick={() => loadAdSets(c)}>{c.name}</td>
                  <td className="px-4 py-2 text-[#64748B]">{OBJECTIVE_LABELS[c.objective]?.split(' — ')[0] || c.objective}</td>
                  <td className="px-4 py-2"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-2 text-[#64748B]">
                    {c.daily_budget ? `${money(c.daily_budget)}/día` : c.lifetime_budget ? `${money(c.lifetime_budget)} total` : '—'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => loadAdSets(c)}>Ver conjuntos</Button>
                      <Button size="icon" variant="ghost" className="w-7 h-7" title="Editar campaña"
                        onClick={() => { setEditingCampaignId(c.id); setCampaignModalOpen(true); }}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="w-7 h-7" disabled={busyId === c.id} onClick={() => toggleCampaignStatus(c)}
                        title={c.status === 'ACTIVE' ? 'Pausar' : 'Activar'}>
                        {c.status === 'ACTIVE' ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                      </Button>
                      <Button size="icon" variant="ghost" className="w-7 h-7 text-red-500" disabled={busyId === c.id} onClick={() => deleteCampaign(c)} title="Eliminar">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CampaignModal
        open={campaignModalOpen} onClose={() => { setCampaignModalOpen(false); setEditingCampaignId(null); }} saving={saving} setSaving={setSaving}
        editingCampaignId={editingCampaignId}
        onCreate={async (payload) => {
          await api.post('/tenant/meta-ads/campaigns', payload);
          toast.success('Campaña creada (pausada, revísala antes de activarla)');
          setCampaignModalOpen(false);
          await loadCampaigns();
        }}
        onSave={async (campaignId, payload) => {
          await api.patch(`/tenant/meta-ads/campaigns/${campaignId}`, payload);
          toast.success('Campaña actualizada');
          setCampaignModalOpen(false);
          setEditingCampaignId(null);
          await loadCampaigns();
        }}
      />
      <AudiencesModal open={audiencesModalOpen} onClose={() => setAudiencesModalOpen(false)} />
      <PixelModal open={pixelModalOpen} onClose={() => setPixelModalOpen(false)} />
    </div>
  );
}

function CampaignModal({ open, onClose, onCreate, onSave, saving, setSaving, editingCampaignId }) {
  const [name, setName] = useState('');
  const [objective, setObjective] = useState('OUTCOME_TRAFFIC');
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(''); setObjective('OUTCOME_TRAFFIC');
    if (editingCampaignId) {
      setLoadingDetail(true);
      api.get(`/tenant/meta-ads/campaigns/${editingCampaignId}`).then(({ data }) => {
        setName(data.name || '');
        setObjective(data.objective || 'OUTCOME_TRAFFIC');
      }).catch((e) => {
        toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cargar la campaña');
      }).finally(() => setLoadingDetail(false));
    }
  }, [open, editingCampaignId]);

  const submit = async () => {
    if (!name.trim()) { toast.error('Ponle un nombre a la campaña'); return; }
    setSaving(true);
    try {
      if (editingCampaignId) {
        await onSave(editingCampaignId, { name: name.trim() });
      } else {
        await onCreate({ name: name.trim(), objective });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{editingCampaignId ? 'Editar campaña' : 'Nueva campaña de Meta Ads'}</DialogTitle></DialogHeader>
        {loadingDetail ? (
          <div className="py-10 flex items-center justify-center gap-2 text-sm text-[#64748B]">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando la campaña…
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Nombre de la campaña</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ej. Promoción de verano" />
              <p className="text-[11px] text-[#94A3B8] mt-1">Solo para identificarla en tu lista — la gente nunca la ve.</p>
            </div>
            <div>
              <Label className="text-xs">¿Qué quieres lograr?</Label>
              {editingCampaignId ? (
                <>
                  <p className="text-sm text-[#0F172A] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-3 py-2">{OBJECTIVE_LABELS[objective] || objective}</p>
                  <p className="text-[11px] text-[#94A3B8] mt-1">Meta no permite cambiar el objetivo de una campaña ya creada — si necesitas otro objetivo, crea una campaña nueva.</p>
                </>
              ) : (
                <>
                  <Select value={objective} onValueChange={setObjective}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(OBJECTIVE_LABELS).map(([k, label]) => (
                        <SelectItem key={k} value={k}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-[#94A3B8] mt-1">Define cómo Meta optimiza tus anuncios — a quién se los muestra primero. No se puede cambiar después de crear la campaña.</p>
                </>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving || loadingDetail} className="gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editingCampaignId ? 'Guardar cambios' : 'Crear campaña'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TargetingSearchBox({ kind, label, placeholder, selected, onAdd, onRemove }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get('/tenant/meta-ads/targeting/search', { params: { q: query.trim(), kind } });
        setResults((data.results || []).filter(r => !selected.some(s => s.id === r.id)));
      } catch (e) {
        // silent — search-as-you-type shouldn't toast on every keystroke
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, kind]);

  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-[#94A3B8]" />
        <Input className="pl-8" value={query} onChange={e => setQuery(e.target.value)} placeholder={placeholder} />
      </div>
      {searching && <p className="text-[11px] text-[#94A3B8] mt-1">Buscando en Meta…</p>}
      {results.length > 0 && (
        <div className="mt-1 border border-[#E2E8F0] rounded-lg overflow-hidden max-h-36 overflow-y-auto">
          {results.map(r => (
            <button key={r.id} type="button" className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-[#F8FAFC] flex items-center justify-between"
              onClick={() => { onAdd(r); setQuery(''); setResults([]); }}>
              <span>{r.name}</span>
              <Plus className="w-3 h-3 text-[#94A3B8] shrink-0" />
            </button>
          ))}
        </div>
      )}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selected.map(s => (
            <Badge key={s.id} className="bg-[#EEF2FF] text-[#4338CA] gap-1 pr-1">
              {s.name}
              <button type="button" onClick={() => onRemove(s.id)} className="hover:text-red-600"><X className="w-3 h-3" /></button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function AdSetModal({ open, onClose, onCreate, onSave, saving, setSaving, campaignObjective, editingAdSetId }) {
  const [name, setName] = useState('');
  // Meta requires every ad set's optimization to match its campaign's
  // objective — this used to be a free selector here, which let the
  // tenant pick an objective that conflicted with the campaign's real
  // one and got rejected by Meta ("la optimización debe ser la misma").
  // It's locked to the parent campaign now, never independently chosen.
  const objective = campaignObjective;
  const objectiveSupported = !!OBJECTIVE_LABELS[objective];
  const [budgetType, setBudgetType] = useState('daily'); // daily | lifetime
  const [budget, setBudget] = useState('100');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [country, setCountry] = useState('MX');
  const [ageMin, setAgeMin] = useState('18');
  const [ageMax, setAgeMax] = useState('65');
  const [gender, setGender] = useState('all'); // all | men | women
  const [interests, setInterests] = useState([]);
  const [behaviors, setBehaviors] = useState([]);
  const [metaAudiences, setMetaAudiences] = useState([]);
  const [selectedAudienceIds, setSelectedAudienceIds] = useState([]);
  const [advantageAudience, setAdvantageAudience] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  // Hotfix 2026-07-19 — WhatsApp destination. Meta's own docs make this an
  // AD-SET-level choice (destination_type), locked once created just like
  // the objective — never editable afterward.
  const [destinationType, setDestinationType] = useState('link'); // 'link' | 'whatsapp'
  const [waInfo, setWaInfo] = useState(null); // {number, page_connected}
  const [waLoading, setWaLoading] = useState(false);
  const destinationSupported = objective === 'OUTCOME_TRAFFIC' || objective === 'OUTCOME_ENGAGEMENT';

  useEffect(() => {
    if (!open) return;
    setName(''); setBudgetType('daily'); setBudget('100');
    setStartDate(''); setEndDate('');
    setCountry('MX'); setAgeMin('18'); setAgeMax('65'); setGender('all');
    setInterests([]); setBehaviors([]); setSelectedAudienceIds([]); setAdvantageAudience(false);
    setDestinationType('link'); setWaInfo(null);
    api.get('/tenant/meta-ads/audiences/meta').then(({ data }) => setMetaAudiences(data.audiences || [])).catch(() => setMetaAudiences([]));
    if (editingAdSetId) {
      setLoadingDetail(true);
      api.get(`/tenant/meta-ads/adsets/${editingAdSetId}`).then(({ data }) => {
        setName(data.name || '');
        setBudgetType(data.budget_type || 'daily');
        setBudget(String(data.budget_amount ?? '100'));
        setStartDate(data.start_time ? data.start_time.slice(0, 10) : '');
        setEndDate(data.end_time ? data.end_time.slice(0, 10) : '');
        setCountry((data.countries && data.countries[0]) || 'MX');
        setAgeMin(String(data.age_min ?? '18'));
        setAgeMax(String(data.age_max ?? '65'));
        const g = data.genders;
        setGender(!g || !g.length ? 'all' : (g.includes(1) && !g.includes(2)) ? 'men' : (g.includes(2) && !g.includes(1)) ? 'women' : 'all');
        setInterests(data.interests || []);
        setBehaviors(data.behaviors || []);
        setSelectedAudienceIds(data.custom_audience_ids || []);
        setAdvantageAudience(!!data.advantage_audience);
        setDestinationType(data.destination_type === 'WHATSAPP' ? 'whatsapp' : 'link');
      }).catch((e) => {
        toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cargar el conjunto de anuncios');
      }).finally(() => setLoadingDetail(false));
    }
  }, [open, editingAdSetId]);

  useEffect(() => {
    if (!open || destinationType !== 'whatsapp' || waInfo) return;
    setWaLoading(true);
    api.get('/tenant/meta-ads/whatsapp-number').then(({ data }) => setWaInfo(data))
      .catch(() => setWaInfo({ number: null, page_connected: false }))
      .finally(() => setWaLoading(false));
  }, [open, destinationType, waInfo]);

  const submit = async () => {
    if (!objectiveSupported) { toast.error('Esta campaña usa un objetivo que esta herramienta no administra todavía'); return; }
    if (!name.trim()) { toast.error('Ponle un nombre al conjunto de anuncios'); return; }
    const amount = parseFloat(budget);
    if (!amount || amount <= 0) { toast.error('El presupuesto debe ser mayor a cero'); return; }
    if (budgetType === 'lifetime' && !endDate) { toast.error('Un presupuesto de por vida necesita fecha de fin'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(), budget_amount: amount, budget_type: budgetType,
        start_time: startDate ? `${startDate}T00:00:00+0000` : null,
        end_time: endDate ? `${endDate}T23:59:59+0000` : null,
        countries: [country],
        age_min: parseInt(ageMin, 10) || 18, age_max: parseInt(ageMax, 10) || 65,
        genders: gender === 'all' ? null : gender === 'men' ? [1] : [2],
        interests: interests.length ? interests.map(i => ({ id: i.id, name: i.name })) : null,
        behaviors: behaviors.length ? behaviors.map(b => ({ id: b.id, name: b.name })) : null,
        custom_audience_ids: selectedAudienceIds.length ? selectedAudienceIds : null,
        advantage_audience: advantageAudience,
      };
      if (editingAdSetId) {
        await onSave(editingAdSetId, payload);
      } else {
        await onCreate({
          ...payload, objective,
          destination_type: destinationSupported && destinationType === 'whatsapp' ? 'whatsapp' : null,
        });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editingAdSetId ? 'Editar conjunto de anuncios' : 'Nuevo conjunto de anuncios'}</DialogTitle></DialogHeader>
        {loadingDetail ? (
          <div className="py-10 flex items-center justify-center gap-2 text-sm text-[#64748B]">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando el conjunto de anuncios…
          </div>
        ) : (
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs">Nombre del conjunto</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ej. Mujeres 25-45 CDMX" />
            <p className="text-[11px] text-[#94A3B8] mt-1">Solo para identificarlo en tu lista — describe a quién le apunta (ej. "Mamás 30-45 Guadalajara"). La gente nunca lo ve.</p>
          </div>
          <div>
            <Label className="text-xs">Objetivo (heredado de la campaña)</Label>
            {objectiveSupported ? (
              <p className="text-sm text-[#0F172A] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-3 py-2">{OBJECTIVE_LABELS[objective]}</p>
            ) : (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-start gap-1.5">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                Esta campaña tiene un objetivo que esta herramienta no administra todavía (objetivo: {objective || 'desconocido'}) — probablemente fue creada desde Meta Ads Manager o Marketplace. Crea el conjunto directamente en Meta Ads Manager.
              </p>
            )}
            <p className="text-[11px] text-[#94A3B8] mt-1">Todos los conjuntos de una campaña deben optimizarse igual que ella — Meta lo exige. No se puede cambiar aquí.</p>
          </div>
          {destinationSupported && (
            <div>
              <Label className="text-xs">¿A dónde llega la gente al tocar el anuncio?</Label>
              <Select value={destinationType} onValueChange={setDestinationType} disabled={!!editingAdSetId}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="link">A un enlace (tu sitio web, WhatsApp escrito a mano, etc.)</SelectItem>
                  <SelectItem value="whatsapp">Directo a un chat de WhatsApp</SelectItem>
                </SelectContent>
              </Select>
              {editingAdSetId ? (
                <p className="text-[11px] text-[#94A3B8] mt-1">El destino no se puede cambiar después de crear el conjunto — igual que el objetivo.</p>
              ) : destinationType === 'whatsapp' ? (
                waLoading ? (
                  <p className="text-[11px] text-[#94A3B8] mt-1 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Revisando tu número de WhatsApp conectado…</p>
                ) : waInfo?.number ? (
                  <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2 mt-1">
                    Los mensajes llegarán al número vinculado a tu página: <strong>{waInfo.number}</strong>.
                  </p>
                ) : (
                  <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 mt-1 space-y-1.5">
                    <p className="flex items-start gap-1.5"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      {waInfo?.page_connected
                        ? 'Tu página de Facebook todavía no tiene un número de WhatsApp Business vinculado — este anuncio no podrá activarse hasta que lo conectes.'
                        : 'Primero conecta tu Fan Page de Facebook en Integraciones.'}
                    </p>
                    <p className="pl-5">Para vincularlo: en <strong>Meta Business Manager</strong> → Cuentas → WhatsApp → conecta un número de <strong>WhatsApp Business</strong> (no una cuenta personal) y asócialo a tu página. Puede tardar unos minutos en reflejarse aquí.</p>
                  </div>
                )
              ) : (
                <p className="text-[11px] text-[#94A3B8] mt-1">"Directo a un chat de WhatsApp" usa el número de WhatsApp Business vinculado a tu página de Meta — Meta abre la conversación automáticamente, sin que tengas que escribir un enlace.</p>
              )}
            </div>
          )}
          <div>
            <Label className="text-xs">Tipo de presupuesto</Label>
            <Select value={budgetType} onValueChange={setBudgetType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Diario — corre indefinido hasta que lo pauses</SelectItem>
                <SelectItem value="lifetime">Total (de por vida) — se reparte entre una fecha de inicio y una de fin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{budgetType === 'lifetime' ? 'Presupuesto total (en tu moneda)' : 'Presupuesto diario (en tu moneda)'}</Label>
            <Input type="number" min="1" step="0.01" value={budget} onChange={e => setBudget(e.target.value)} />
            <p className="text-[11px] text-[#94A3B8] mt-1">
              {budgetType === 'lifetime'
                ? 'Lo que Meta puede gastar en total durante todo el periodo — se distribuye automáticamente entre el inicio y el fin.'
                : 'Lo que Meta puede gastar cada día mientras el conjunto esté activo.'}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Fecha de inicio (opcional)</Label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
              <p className="text-[11px] text-[#94A3B8] mt-1">Vacío = empieza en cuanto lo actives.</p>
            </div>
            <div>
              <Label className="text-xs">Fecha de fin {budgetType === 'lifetime' ? '(obligatoria)' : '(opcional)'}</Label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
              <p className="text-[11px] text-[#94A3B8] mt-1">{budgetType === 'lifetime' ? 'Requerida para presupuesto de por vida.' : 'Vacío = corre indefinidamente.'}</p>
            </div>
          </div>
          <div>
            <Label className="text-xs">País donde se muestra</Label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="MX">México</SelectItem>
                <SelectItem value="US">Estados Unidos</SelectItem>
                <SelectItem value="CO">Colombia</SelectItem>
                <SelectItem value="AR">Argentina</SelectItem>
                <SelectItem value="CL">Chile</SelectItem>
                <SelectItem value="PE">Perú</SelectItem>
                <SelectItem value="ES">España</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[#94A3B8] mt-1">El país donde vive la gente a la que le quieres mostrar el anuncio — hoy solo se puede elegir uno.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Edad mínima</Label>
              <Input type="number" min="13" max="65" value={ageMin} onChange={e => setAgeMin(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Edad máxima</Label>
              <Input type="number" min="13" max="65" value={ageMax} onChange={e => setAgeMax(e.target.value)} />
            </div>
          </div>
          <p className="text-[11px] text-[#94A3B8] -mt-2">Rango de edad de las personas que pueden ver el anuncio — entre más amplio, más alcance pero menos enfocado.</p>
          <div>
            <Label className="text-xs">Género</Label>
            <Select value={gender} onValueChange={setGender}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="men">Hombres</SelectItem>
                <SelectItem value="women">Mujeres</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[#94A3B8] mt-1">Deja "Todos" salvo que tu producto o servicio sea claramente para un solo género.</p>
          </div>
          <div className="border-t border-[#F1F5F9] pt-3 space-y-3">
            <p className="text-xs font-semibold text-[#0F172A] flex items-center gap-1.5"><Search className="w-3.5 h-3.5" /> Segmentación avanzada (opcional)</p>
            <label className="flex items-start gap-2 border-2 border-[#EEF2FF] bg-[#EEF2FF]/40 rounded-lg p-2.5 cursor-pointer hover:bg-[#EEF2FF]">
              <input type="checkbox" className="mt-0.5" checked={advantageAudience} onChange={(e) => setAdvantageAudience(e.target.checked)} />
              <span>
                <span className="text-xs font-medium text-[#0F172A] flex items-center gap-1"><Sparkles className="w-3 h-3 text-[#4338CA]" /> Dejar que Meta amplíe la audiencia (Advantage+)</span>
                <span className="text-[11px] text-[#64748B] block mt-0.5">Meta puede mostrar el anuncio a gente fuera de tu segmentación si detecta que le va a interesar — suele mejorar resultados a cambio de un poco menos de control. Recomendado activado si no estás seguro.</span>
              </span>
            </label>
            <TargetingSearchBox kind="interests" label="Intereses (ej. 'cocina', 'yoga', 'mascotas')"
              placeholder="Escribe para buscar un interés…" selected={interests}
              onAdd={(r) => setInterests(prev => [...prev, r])}
              onRemove={(id) => setInterests(prev => prev.filter(i => i.id !== id))} />
            <TargetingSearchBox kind="behaviors" label="Comportamientos (ej. 'viajeros frecuentes')"
              placeholder="Escribe para buscar un comportamiento…" selected={behaviors}
              onAdd={(r) => setBehaviors(prev => [...prev, r])}
              onRemove={(id) => setBehaviors(prev => prev.filter(b => b.id !== id))} />
            <p className="text-[11px] text-[#94A3B8]">Si no agregas ninguno, Meta le muestra el anuncio a todos los que cumplan edad/género/país de arriba — agregar intereses o comportamientos hace la audiencia más específica. Con Advantage+ activado, estos siguen siendo un punto de partida: Meta puede salirse de ellos si encuentra mejores resultados.</p>
            {metaAudiences.length > 0 && (
              <div>
                <Label className="text-xs">Públicos personalizados o similares (opcional)</Label>
                <p className="text-[11px] text-[#94A3B8] mb-1.5">Créalos primero con el botón "Públicos" — aquí solo eliges cuáles usar en este conjunto.</p>
                <div className="border border-[#E2E8F0] rounded-lg divide-y divide-[#F1F5F9] max-h-32 overflow-y-auto">
                  {metaAudiences.map(a => (
                    <label key={a.id} className="flex items-center gap-2 px-2.5 py-1.5 text-xs cursor-pointer hover:bg-[#F8FAFC]">
                      <input type="checkbox" checked={selectedAudienceIds.includes(a.id)}
                        onChange={(e) => setSelectedAudienceIds(prev => e.target.checked ? [...prev, a.id] : prev.filter(x => x !== a.id))} />
                      <span className="flex-1">{a.name}</span>
                      <span className="text-[#94A3B8]">{a.subtype === 'LOOKALIKE' ? 'Similar' : 'Personalizado'}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving || loadingDetail || !objectiveSupported} className="gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editingAdSetId ? 'Guardar cambios' : 'Crear conjunto'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeadFormPicker({ selectedFormId, onSelect }) {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newFormOpen, setNewFormOpen] = useState(false);
  const [formName, setFormName] = useState('');
  const [askName, setAskName] = useState(true);
  const [askEmail, setAskEmail] = useState(true);
  const [askPhone, setAskPhone] = useState(true);
  const [customQuestion, setCustomQuestion] = useState('');
  const [privacyUrl, setPrivacyUrl] = useState('');

  const loadForms = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tenant/meta-ads/lead-forms');
      setForms(data.forms || []);
    } catch (e) {
      setForms([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadForms(); }, [loadForms]);

  const createForm = async () => {
    if (!formName.trim()) { toast.error('Ponle un nombre al formulario'); return; }
    if (!askName && !askEmail && !askPhone && !customQuestion.trim()) {
      toast.error('El formulario necesita al menos una pregunta'); return;
    }
    if (!privacyUrl.trim()) { toast.error('Meta exige un enlace a tu política de privacidad'); return; }
    setCreating(true);
    try {
      const questions = [];
      if (askName) questions.push({ type: 'full_name' });
      if (askEmail) questions.push({ type: 'email' });
      if (askPhone) questions.push({ type: 'phone' });
      if (customQuestion.trim()) questions.push({ type: 'custom', label: customQuestion.trim() });
      const { data } = await api.post('/tenant/meta-ads/lead-forms', {
        name: formName.trim(), questions, privacy_policy_url: privacyUrl.trim(),
      });
      toast.success('Formulario creado en Meta');
      setNewFormOpen(false); setFormName(''); setCustomQuestion(''); setPrivacyUrl('');
      await loadForms();
      onSelect(data.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="border border-[#E2E8F0] rounded-lg p-3 bg-[#FEFCE8] space-y-2">
      <p className="text-xs font-semibold text-[#0F172A] flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Formulario instantáneo (obligatorio para Leads)</p>
      <p className="text-[11px] text-[#64748B]">Al hacer clic en el anuncio, la persona llena este formulario sin salir de Facebook/Instagram — los datos llegan directo a tu CRM.</p>
      {loading ? (
        <p className="text-xs text-[#94A3B8]">Cargando formularios…</p>
      ) : (
        <Select value={selectedFormId || ''} onValueChange={(v) => v === '__new__' ? setNewFormOpen(true) : onSelect(v)}>
          <SelectTrigger><SelectValue placeholder="Elige un formulario" /></SelectTrigger>
          <SelectContent>
            {forms.map(f => (
              <SelectItem key={f.id} value={f.id}>{f.name} {f.leads_count ? `(${f.leads_count} leads)` : ''}</SelectItem>
            ))}
            <SelectItem value="__new__">+ Crear formulario nuevo</SelectItem>
          </SelectContent>
        </Select>
      )}
      {newFormOpen && (
        <div className="border-t border-[#F1F5F9] pt-2 space-y-2">
          <Input value={formName} onChange={e => setFormName(e.target.value)} placeholder="Nombre del formulario, ej. 'Cotización rápida'" />
          <div className="flex flex-wrap gap-3 text-xs">
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={askName} onChange={e => setAskName(e.target.checked)} /> Nombre completo</label>
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={askEmail} onChange={e => setAskEmail(e.target.checked)} /> Email</label>
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={askPhone} onChange={e => setAskPhone(e.target.checked)} /> Teléfono</label>
          </div>
          <Input value={customQuestion} onChange={e => setCustomQuestion(e.target.value)} placeholder="Pregunta extra (opcional), ej. '¿Qué servicio te interesa?'" />
          <Input value={privacyUrl} onChange={e => setPrivacyUrl(e.target.value)} placeholder="Enlace a tu política de privacidad (obligatorio para Meta)" />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setNewFormOpen(false)}>Cancelar</Button>
            <Button size="sm" onClick={createForm} disabled={creating} className="gap-1.5">
              {creating && <Loader2 className="w-4 h-4 animate-spin" />} Crear formulario
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const PLACEMENTS = [
  { key: 'feed', label: 'Feed' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'story', label: 'Stories' },
  { key: 'reel', label: 'Reels' },
];

function PlaceholderMedia({ pictureUrl, isVideo }) {
  return pictureUrl ? (
    <img src={pictureUrl} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
  ) : (
    <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-[#94A3B8]">
      {isVideo ? <VideoIcon className="w-6 h-6" /> : <ImageIcon className="w-6 h-6" />}
      <span className="text-[10px]">{isVideo ? 'Sin video' : 'Sin imagen'}</span>
    </div>
  );
}

function AdPreviewCard({ message, link, pictureUrl, cta, isLeadAdSet, format, extraCount }) {
  const [placement, setPlacement] = useState('feed');
  let host = '';
  try { host = link ? new URL(link).hostname.replace('www.', '') : ''; } catch (e) { host = link; }
  const ctaLabel = isLeadAdSet ? 'Registrarse' : (CTA_LABELS[cta] || 'Más información');
  const isVideo = format === 'video';

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-4 gap-1 bg-[#F1F5F9] rounded-lg p-1">
        {PLACEMENTS.map(p => (
          <button key={p.key} type="button" onClick={() => setPlacement(p.key)}
            className={`text-[11px] py-1.5 rounded-md transition-colors ${placement === p.key ? 'bg-white text-[#0F172A] shadow-sm font-medium' : 'text-[#64748B] hover:text-[#0F172A]'}`}>
            {p.label}
          </button>
        ))}
      </div>

      {placement === 'feed' && (
        <div className="border border-[#E2E8F0] rounded-lg overflow-hidden bg-white max-w-xs mx-auto shadow-sm">
          <div className="px-3 py-2 flex items-center gap-2 border-b border-[#F1F5F9]">
            <div className="w-6 h-6 rounded-full bg-[#E2E8F0]" />
            <div className="flex-1">
              <div className="h-2 w-20 bg-[#E2E8F0] rounded" />
              <div className="h-1.5 w-10 bg-[#F1F5F9] rounded mt-1" />
            </div>
          </div>
          <p className="px-3 py-2 text-xs text-[#0F172A] whitespace-pre-wrap break-words min-h-[32px]">
            {message || <span className="text-[#CBD5E1]">El texto de tu anuncio aparecerá aquí…</span>}
          </p>
          <div className="bg-[#F1F5F9] aspect-[1.91/1] flex items-center justify-center overflow-hidden relative">
            <PlaceholderMedia pictureUrl={pictureUrl} isVideo={isVideo} />
            {extraCount > 0 && <span className="absolute bottom-1.5 right-1.5 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">+{extraCount}</span>}
          </div>
          <div className="px-3 py-2 flex items-center justify-between bg-white">
            <div className="min-w-0">
              <p className="text-[10px] text-[#94A3B8] uppercase truncate">{host || 'tusitio.com'}</p>
              <p className="text-xs font-medium text-[#0F172A] truncate">{message ? message.slice(0, 40) : 'Título del anuncio'}</p>
            </div>
            <span className="shrink-0 ml-2 text-[11px] font-semibold px-2.5 py-1.5 rounded bg-[#F1F5F9] text-[#0F172A]">{ctaLabel}</span>
          </div>
        </div>
      )}

      {placement === 'instagram' && (
        <div className="border border-[#E2E8F0] rounded-lg overflow-hidden bg-white max-w-xs mx-auto shadow-sm">
          <div className="px-3 py-2 flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-amber-400 via-pink-500 to-purple-600 p-[1.5px]">
              <div className="w-full h-full rounded-full bg-white" />
            </div>
            <div className="flex-1">
              <div className="h-2 w-16 bg-[#E2E8F0] rounded" />
              <p className="text-[9px] text-[#94A3B8] mt-0.5">Patrocinado</p>
            </div>
            <MoreHorizontal className="w-4 h-4 text-[#94A3B8]" />
          </div>
          <div className="bg-[#F1F5F9] aspect-square flex items-center justify-center overflow-hidden relative">
            <PlaceholderMedia pictureUrl={pictureUrl} isVideo={isVideo} />
            {extraCount > 0 && <span className="absolute bottom-1.5 right-1.5 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">+{extraCount}</span>}
          </div>
          <div className="px-3 py-2 flex items-center justify-between">
            <div className="flex items-center gap-3 text-[#0F172A]">
              <Heart className="w-4.5 h-4.5" /><MessageCircle className="w-4.5 h-4.5" /><Send className="w-4.5 h-4.5" />
            </div>
            <Bookmark className="w-4.5 h-4.5 text-[#0F172A]" />
          </div>
          <p className="px-3 pb-2 text-xs text-[#0F172A] whitespace-pre-wrap break-words">
            {message || <span className="text-[#CBD5E1]">El texto de tu anuncio aparecerá aquí…</span>}
          </p>
          <div className="px-3 pb-2.5">
            <span className="text-[11px] font-semibold text-[#4338CA] underline">{ctaLabel}</span>
          </div>
        </div>
      )}

      {(placement === 'story' || placement === 'reel') && (
        <div className="relative aspect-[9/16] max-w-[180px] mx-auto rounded-xl overflow-hidden bg-[#0F172A] shadow-sm">
          <PlaceholderMedia pictureUrl={pictureUrl} isVideo={isVideo} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/70" />
          {placement === 'story' && (
            <div className="absolute top-1.5 left-1.5 right-1.5 flex gap-1">
              <div className="h-0.5 flex-1 bg-white/40 rounded"><div className="h-full w-2/3 bg-white rounded" /></div>
            </div>
          )}
          <div className="absolute top-3 left-2 flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-white/90" />
            <div className="h-1.5 w-12 bg-white/70 rounded" />
          </div>
          {placement === 'reel' && (
            <div className="absolute right-1.5 bottom-10 flex flex-col items-center gap-2.5 text-white">
              <Heart className="w-4 h-4" /><MessageCircle className="w-4 h-4" /><Send className="w-4 h-4" /><Volume2 className="w-4 h-4" />
            </div>
          )}
          <div className="absolute bottom-0 left-0 right-0 p-2.5 space-y-1.5">
            <p className="text-[10px] text-white whitespace-pre-wrap break-words line-clamp-3">
              {message || <span className="text-white/50">El texto de tu anuncio aparecerá aquí…</span>}
            </p>
            <span className="inline-block text-[10px] font-semibold px-2 py-1 rounded bg-white text-[#0F172A]">{ctaLabel}</span>
          </div>
        </div>
      )}
      {extraCount > 0 && placement !== 'feed' && placement !== 'instagram' && (
        <p className="text-[11px] text-[#64748B] text-center">+ {extraCount} elemento(s) más en el carrusel</p>
      )}
    </div>
  );
}

function MediaLibraryPicker({ open, onClose, mediaType, onSelect }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [preparingId, setPreparingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const inputRef = useRef(null);

  const loadAssets = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tenant/meta-ads/media-library');
      setAssets(data.assets || []);
    } catch (e) {
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) loadAssets(); }, [open, loadAssets]);

  const filtered = assets.filter(a => (mediaType === 'video' ? a.is_video : !a.is_video));

  const uploadNew = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post('/tenant/meta-ads/media-library', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadAssets();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo subir el archivo');
    } finally {
      setUploading(false);
    }
  };

  const pollVideoReady = async (assetId) => {
    for (let i = 0; i < 20; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const { data } = await api.get(`/tenant/meta-ads/media-library/${assetId}/video-status`);
        if (data.status === 'ready') return data.video_id;
      } catch (e) { return null; }
    }
    return null;
  };

  const pickAsset = async (asset) => {
    setPreparingId(asset.id);
    try {
      const { data } = await api.post(`/tenant/meta-ads/media-library/${asset.id}/prepare`);
      if (asset.is_video) {
        if (data.status === 'ready') {
          onSelect({ videoId: data.video_id });
        } else {
          toast.info('Meta está procesando este video, un momento…');
          const readyId = await pollVideoReady(asset.id);
          if (!readyId) { toast.error('El video sigue procesándose — intenta de nuevo en un momento'); return; }
          onSelect({ videoId: readyId });
        }
      } else {
        onSelect({ hash: data.hash, previewUrl: data.preview_url || asset.thumbnail_url });
      }
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo usar este archivo');
    } finally {
      setPreparingId(null);
    }
  };

  const deleteAsset = async (asset, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar este archivo de tu biblioteca? Esto no afecta anuncios que ya lo estén usando.')) return;
    setDeletingId(asset.id);
    try {
      await api.delete(`/tenant/meta-ads/media-library/${asset.id}`);
      await loadAssets();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Images className="w-4 h-4" /> Tu biblioteca de {mediaType === 'video' ? 'videos' : 'imágenes'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-[11px] text-[#94A3B8]">Sube un archivo una vez y reutilízalo en cualquier anuncio nuevo o al editar uno existente, sin volver a subirlo.</p>
          <input ref={inputRef} type="file" accept={mediaType === 'video' ? 'video/mp4,video/quicktime,video/webm' : 'image/jpeg,image/png,image/webp'} className="hidden"
            onChange={(e) => uploadNew(e.target.files?.[0])} />
          <Button size="sm" variant="outline" disabled={uploading} onClick={() => inputRef.current?.click()} className="gap-1.5">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Subir {mediaType === 'video' ? 'video' : 'imagen'} nuevo a la biblioteca
          </Button>
          {loading ? (
            <p className="text-xs text-[#94A3B8]">Cargando tu biblioteca…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-[#94A3B8] text-center py-8">
              Todavía no subes {mediaType === 'video' ? 'videos' : 'imágenes'} a tu biblioteca — sube el primero arriba y podrás reutilizarlo después.
            </p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {filtered.map(a => (
                <div key={a.id} className="relative group border border-[#E2E8F0] rounded-lg overflow-hidden aspect-square bg-[#F1F5F9]">
                  <button type="button" onClick={() => pickAsset(a)} disabled={preparingId === a.id} className="w-full h-full block">
                    <img src={a.thumbnail_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  </button>
                  {a.is_video && <VideoIcon className="absolute bottom-1 left-1 w-3.5 h-3.5 text-white drop-shadow pointer-events-none" />}
                  {preparingId === a.id && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center pointer-events-none">
                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                    </div>
                  )}
                  <button type="button" onClick={(e) => deleteAsset(a, e)} disabled={deletingId === a.id}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600">
                    {deletingId === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cerrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImageUploader({ label, help, previewUrl, onUploaded, compact }) {
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/tenant/meta-ads/media/image', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUploaded({ hash: data.hash, previewUrl: data.preview_url });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo subir la imagen');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {label && <Label className="text-xs">{label}</Label>}
      <div className={`border border-dashed border-[#CBD5E1] rounded-lg flex items-center gap-3 ${compact ? 'p-2' : 'p-3'}`}>
        {previewUrl ? (
          <img src={previewUrl} alt="" className={`${compact ? 'w-10 h-10' : 'w-16 h-16'} object-cover rounded`} />
        ) : (
          <div className={`${compact ? 'w-10 h-10' : 'w-16 h-16'} bg-[#F1F5F9] rounded flex items-center justify-center shrink-0`}>
            <ImageIcon className="w-4 h-4 text-[#94A3B8]" />
          </div>
        )}
        <div className="flex-1 min-w-0 flex flex-wrap gap-1.5">
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])} />
          <Button size="sm" variant="outline" disabled={uploading} onClick={() => inputRef.current?.click()} className="gap-1.5">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {previewUrl ? 'Cambiar imagen' : 'Subir nueva'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setPickerOpen(true)} className="gap-1.5">
            <Images className="w-3.5 h-3.5" /> Mi biblioteca
          </Button>
        </div>
      </div>
      {help && <p className="text-[11px] text-[#94A3B8] mt-1">{help}</p>}
      <MediaLibraryPicker open={pickerOpen} onClose={() => setPickerOpen(false)} mediaType="image"
        onSelect={(r) => onUploaded({ hash: r.hash, previewUrl: r.previewUrl })} />
    </div>
  );
}

function VideoUploader({ videoId, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const inputRef = useRef(null);

  const pollStatus = async (id) => {
    for (let i = 0; i < 20; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const { data } = await api.get(`/tenant/meta-ads/media/video-status/${id}`);
        if (data.status === 'ready') { setStatusText(''); return; }
        setStatusText(`Meta está procesando el video… (${data.status})`);
      } catch (e) { return; }
    }
    setStatusText('');
  };

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setStatusText('Subiendo video…');
    try {
      const form = new FormData();
      form.append('file', file);
      const { data: stored } = await api.post('/tenant/social-posts/media', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setStatusText('Enviando video a Meta…');
      const { data: video } = await api.post('/tenant/meta-ads/media/video-from-url', {
        media_url: stored.media_url, name: file.name,
      });
      onUploaded(video.video_id);
      setStatusText('Meta está procesando el video…');
      await pollStatus(video.video_id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo subir el video');
      setStatusText('');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Label className="text-xs">Video</Label>
      <div className="border border-dashed border-[#CBD5E1] rounded-lg p-3 flex items-center gap-3">
        <div className="w-16 h-16 bg-[#F1F5F9] rounded flex items-center justify-center shrink-0">
          <VideoIcon className="w-5 h-5 text-[#94A3B8]" />
        </div>
        <div className="flex-1 min-w-0">
          <input ref={inputRef} type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])} />
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant="outline" disabled={uploading} onClick={() => inputRef.current?.click()} className="gap-1.5">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {videoId ? 'Cambiar video' : 'Subir nuevo'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPickerOpen(true)} className="gap-1.5">
              <Images className="w-3.5 h-3.5" /> Mi biblioteca
            </Button>
          </div>
          {videoId && !statusText && <p className="text-[11px] text-emerald-600 mt-1">Video listo ✓</p>}
          {statusText && <p className="text-[11px] text-[#94A3B8] mt-1">{statusText}</p>}
        </div>
      </div>
      <p className="text-[11px] text-[#94A3B8] mt-1">MP4, MOV o WebM. Formato vertical 9:16 recomendado para Reels/Stories, horizontal para Feed.</p>
      <MediaLibraryPicker open={pickerOpen} onClose={() => setPickerOpen(false)} mediaType="video"
        onSelect={(r) => { onUploaded(r.videoId); setStatusText(''); }} />
    </div>
  );
}

function CarouselCardEditor({ items, onChange, defaultLink }) {
  const updateItem = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const addItem = () => onChange([...items, { image_hash: null, previewUrl: null, name: '', description: '', link: '' }]);
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs">Tarjetas del carrusel ({items.length}/10)</Label>
        <Button size="sm" variant="outline" onClick={addItem} disabled={items.length >= 10} className="gap-1"><Plus className="w-3.5 h-3.5" /> Agregar tarjeta</Button>
      </div>
      <p className="text-[11px] text-[#94A3B8]">Mínimo 2 tarjetas, máximo 10 — la gente las desliza una por una.</p>
      {items.map((item, idx) => (
        <div key={idx} className="border border-[#E2E8F0] rounded-lg p-3 space-y-2 bg-[#F8FAFC]">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-[#0F172A]">Tarjeta {idx + 1}</span>
            <Button size="icon" variant="ghost" className="w-6 h-6 text-red-500" onClick={() => removeItem(idx)} disabled={items.length <= 2}>
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
          <ImageUploader compact previewUrl={item.previewUrl}
            onUploaded={({ hash, previewUrl }) => updateItem(idx, { image_hash: hash, previewUrl })} />
          <Input value={item.name} onChange={e => updateItem(idx, { name: e.target.value })} placeholder="Título de la tarjeta" />
          <Input value={item.link} onChange={e => updateItem(idx, { link: e.target.value })}
            placeholder={`Enlace propio (opcional — si se deja vacío usa ${defaultLink || 'el enlace principal'})`} />
        </div>
      ))}
    </div>
  );
}

const AD_FORMAT_LABELS = {
  single_image: 'Imagen única',
  video: 'Video',
  carousel: 'Carrusel',
};

function AdModal({ open, onClose, onCreate, onSave, saving, setSaving, isLeadAdSet, isWhatsAppAdSet, editingAdId }) {
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [link, setLink] = useState('');
  const [cta, setCta] = useState('LEARN_MORE');
  const [leadFormId, setLeadFormId] = useState(null);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [format, setFormat] = useState('single_image');
  const [imageHash, setImageHash] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [videoId, setVideoId] = useState(null);
  const [videoTitle, setVideoTitle] = useState('');
  const [videoThumbHash, setVideoThumbHash] = useState(null);
  const [videoThumbPreviewUrl, setVideoThumbPreviewUrl] = useState('');
  const [carouselItems, setCarouselItems] = useState([
    { image_hash: null, previewUrl: null, name: '', description: '', link: '' },
    { image_hash: null, previewUrl: null, name: '', description: '', link: '' },
  ]);
  const [editingExistingMedia, setEditingExistingMedia] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(''); setMessage(''); setCta(isLeadAdSet ? 'SIGN_UP' : 'LEARN_MORE'); setLeadFormId(null);
    setWelcomeMessage('');
    // WhatsApp-destination ads don't ask for a link at all — Meta forces
    // it to a fixed constant server-side, this is just a non-empty
    // placeholder so the existing "link required" validation still passes.
    setLink(isWhatsAppAdSet ? 'https://api.whatsapp.com/send' : '');
    setFormat('single_image'); setImageHash(null); setImagePreviewUrl('');
    setVideoId(null); setVideoTitle(''); setVideoThumbHash(null); setVideoThumbPreviewUrl('');
    setCarouselItems([
      { image_hash: null, previewUrl: null, name: '', description: '', link: '' },
      { image_hash: null, previewUrl: null, name: '', description: '', link: '' },
    ]);
    setEditingExistingMedia(false);
    if (editingAdId) {
      setLoadingDetail(true);
      api.get(`/tenant/meta-ads/ads/${editingAdId}`).then(({ data }) => {
        setName(data.name || '');
        setMessage(data.message || '');
        setLink(data.link || '');
        setCta(data.call_to_action_type || (isLeadAdSet ? 'SIGN_UP' : 'LEARN_MORE'));
        setLeadFormId(data.lead_gen_form_id || null);
        setFormat(data.format || 'single_image');
        if (data.format === 'single_image' || !data.format) {
          setImagePreviewUrl(data.picture_url || '');
        } else {
          // Meta doesn't hand back a re-usable hash for an existing
          // video/carousel — the tenant has to re-confirm the media to
          // save changes, same as re-picking a file would.
          setEditingExistingMedia(true);
          if (data.format === 'video') setVideoTitle(data.title || '');
        }
      }).catch((e) => {
        toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cargar el anuncio');
      }).finally(() => setLoadingDetail(false));
    }
  }, [open, isLeadAdSet, isWhatsAppAdSet, editingAdId]);

  const submit = async () => {
    if (!message.trim() || !link.trim()) {
      toast.error('Completa el texto y el enlace del anuncio');
      return;
    }
    if (!editingAdId && !name.trim()) {
      toast.error('Ponle un nombre interno al anuncio');
      return;
    }
    if (isLeadAdSet && !leadFormId) {
      toast.error('Elige o crea un formulario instantáneo antes de guardar');
      return;
    }
    if (format === 'video' && !videoId) {
      toast.error('Sube un video primero');
      return;
    }
    let normalizedCarouselItems = null;
    if (format === 'carousel') {
      const usable = carouselItems.filter(i => i.image_hash);
      if (usable.length < 2) {
        toast.error('Sube al menos 2 imágenes para el carrusel');
        return;
      }
      normalizedCarouselItems = usable.map(i => ({
        image_hash: i.image_hash, name: i.name || '', description: i.description || '',
        link: i.link?.trim() || null,
      }));
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim() || undefined, message: message.trim(), link: link.trim(),
        call_to_action_type: cta,
        lead_gen_form_id: isLeadAdSet ? leadFormId : null,
        format,
        image_hash: format === 'single_image' ? imageHash : null,
        video_id: format === 'video' ? videoId : null,
        video_title: format === 'video' ? (videoTitle.trim() || null) : null,
        video_thumbnail_hash: format === 'video' ? videoThumbHash : null,
        carousel_items: format === 'carousel' ? normalizedCarouselItems : null,
        destination_type: isWhatsAppAdSet ? 'whatsapp' : null,
        welcome_message: isWhatsAppAdSet ? (welcomeMessage.trim() || null) : null,
      };
      if (editingAdId) {
        await onSave(editingAdId, payload);
      } else {
        await onCreate(payload);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingAdId ? 'Editar anuncio' : 'Nuevo anuncio'}</DialogTitle>
        </DialogHeader>
        {loadingDetail ? (
          <div className="py-10 flex items-center justify-center gap-2 text-sm text-[#64748B]">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando el anuncio…
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6 py-2">
            <div className="space-y-4">
              {editingAdId && (
                <p className="text-[11px] text-[#64748B] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2 flex items-start gap-1.5">
                  <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  Al guardar, Meta crea una versión nueva del anuncio con estos cambios — el historial y las estadísticas del original no se pierden.
                </p>
              )}
              {editingExistingMedia && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 flex items-start gap-1.5">
                  <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  Este anuncio es de tipo {AD_FORMAT_LABELS[format]?.toLowerCase()} — vuelve a subir el video/imágenes abajo para guardar cambios (Meta no permite reutilizar el archivo anterior directamente).
                </p>
              )}
              <div>
                <Label className="text-xs">Nombre interno del anuncio</Label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ej. Anuncio A — foto local" />
                <p className="text-[11px] text-[#94A3B8] mt-1">Solo tú lo ves en CavernicolApp para identificarlo en la lista — nunca aparece frente a la gente.</p>
              </div>

              {!isLeadAdSet && !isWhatsAppAdSet && (
                <div>
                  <Label className="text-xs">Formato del anuncio</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {Object.entries(AD_FORMAT_LABELS).map(([k, label]) => {
                      const Icon = k === 'video' ? VideoIcon : k === 'carousel' ? Images : ImageIcon;
                      return (
                        <button key={k} type="button" onClick={() => setFormat(k)}
                          className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border text-xs transition-colors ${
                            format === k ? 'border-[#0F172A] bg-[#0F172A] text-white' : 'border-[#E2E8F0] text-[#64748B] hover:bg-[#F8FAFC]'}`}>
                          <Icon className="w-4 h-4" /> {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs">Texto del anuncio</Label>
                <textarea className="w-full border border-[#E2E8F0] rounded-lg p-2 text-sm min-h-[90px]"
                  value={message} onChange={e => setMessage(e.target.value)}
                  placeholder="Lo que la gente va a leer en el anuncio" maxLength={2000} />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-[11px] text-[#94A3B8]">Pon lo más importante al inicio — después de ~125 caracteres Meta lo recorta con "Ver más".</p>
                  <span className={`text-[11px] shrink-0 ml-2 ${message.length > 125 ? 'text-amber-600' : 'text-[#94A3B8]'}`}>{message.length}/125</span>
                </div>
              </div>
              {isWhatsAppAdSet ? (
                <div>
                  <p className="text-xs font-medium text-[#0F172A] flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-lg p-2.5">
                    <Info className="w-3.5 h-3.5 shrink-0 text-emerald-600" />
                    Al tocar el anuncio se abre WhatsApp automáticamente — no necesitas poner ningún enlace.
                  </p>
                  <Label className="text-xs mt-3 block">Mensaje de bienvenida (opcional)</Label>
                  <textarea className="w-full border border-[#E2E8F0] rounded-lg p-2 text-sm min-h-[60px]"
                    value={welcomeMessage} onChange={e => setWelcomeMessage(e.target.value)}
                    placeholder="Ej. ¡Hola! Vi tu anuncio, ¿me ayudas con información?" maxLength={300} />
                  <p className="text-[11px] text-[#94A3B8] mt-1">Lo que la persona ve ya escrito en WhatsApp, listo para enviar. Si lo dejas vacío, Meta pone un saludo genérico.</p>
                </div>
              ) : (
                <div>
                  <Label className="text-xs">{isLeadAdSet ? 'Enlace de respaldo (tu sitio web)' : 'Enlace a donde lleva el anuncio'}</Label>
                  <Input value={link} onChange={e => setLink(e.target.value)} placeholder="https://tu-sitio.com" />
                  <p className="text-[11px] text-[#94A3B8] mt-1">
                    {isLeadAdSet
                      ? 'En anuncios de Leads, Meta abre el formulario en vez de este enlace — pero igual necesita uno registrado (usa tu página web o de Facebook).'
                      : 'Debe empezar con https:// y llevar a una página real — puede ser tu sitio web, un WhatsApp (wa.me/...) o tu página de Facebook.'}
                  </p>
                </div>
              )}

              {format === 'single_image' && (
                <ImageUploader label="Imagen del anuncio" previewUrl={imagePreviewUrl}
                  onUploaded={({ hash, previewUrl }) => { setImageHash(hash); setImagePreviewUrl(previewUrl); }}
                  help="Tamaño recomendado 1200×628 px (rectangular) para que no se vea recortada." />
              )}
              {format === 'video' && (
                <div className="space-y-3">
                  <VideoUploader videoId={videoId} onUploaded={setVideoId} />
                  <Input value={videoTitle} onChange={e => setVideoTitle(e.target.value)} placeholder="Título del video (opcional)" />
                  <ImageUploader compact label="Miniatura del video" previewUrl={videoThumbPreviewUrl}
                    onUploaded={({ hash, previewUrl }) => { setVideoThumbHash(hash); setVideoThumbPreviewUrl(previewUrl); }}
                    help="La imagen que se ve antes de que el video empiece a reproducirse." />
                </div>
              )}
              {format === 'carousel' && (
                <CarouselCardEditor items={carouselItems} onChange={setCarouselItems} defaultLink={link} />
              )}

              {!isLeadAdSet && !isWhatsAppAdSet && (
                <div>
                  <Label className="text-xs">Botón del anuncio</Label>
                  <Select value={cta} onValueChange={setCta}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(CTA_LABELS).map(([k, label]) => (
                        <SelectItem key={k} value={k}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-[#94A3B8] mt-1">El texto del botón de acción — elige el que mejor describa qué pasa al hacer clic (ej. "Enviar WhatsApp" si el enlace abre WhatsApp).</p>
                </div>
              )}
              {isLeadAdSet && <LeadFormPicker selectedFormId={leadFormId} onSelect={setLeadFormId} />}
            </div>
            <div className="space-y-2">
              <p className="text-xs font-semibold text-[#0F172A] flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> Vista previa aproximada</p>
              <AdPreviewCard message={message} link={link}
                pictureUrl={format === 'video' ? videoThumbPreviewUrl : (format === 'carousel' ? carouselItems.find(i => i.previewUrl)?.previewUrl : imagePreviewUrl)}
                cta={isWhatsAppAdSet ? 'WHATSAPP_MESSAGE' : cta} isLeadAdSet={isLeadAdSet} format={format}
                extraCount={format === 'carousel' ? Math.max(0, carouselItems.filter(i => i.previewUrl).length - 1) : 0} />
              <p className="text-[11px] text-[#94A3B8] text-center">Cambia de pestaña arriba para ver cómo se vería en cada lugar — es una referencia aproximada, no un espejo exacto de Meta.</p>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving || loadingDetail} className="gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editingAdId ? 'Guardar cambios' : 'Crear anuncio'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AudiencesModal({ open, onClose }) {
  const [audiences, setAudiences] = useState([]);
  const [crmAudiences, setCrmAudiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list'); // list | new_custom | new_lookalike
  const [selectedCrmAudienceId, setSelectedCrmAudienceId] = useState('');
  const [eligibleCount, setEligibleCount] = useState(null);
  const [checkingEligible, setCheckingEligible] = useState(false);
  const [customName, setCustomName] = useState('');
  const [creating, setCreating] = useState(false);
  const [lookalikeOriginId, setLookalikeOriginId] = useState('');
  const [lookalikeName, setLookalikeName] = useState('');
  const [lookalikeRatio, setLookalikeRatio] = useState('5');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [audRes, crmRes] = await Promise.all([
        api.get('/tenant/meta-ads/audiences/meta'),
        api.get('/tenant/audiences').catch(() => ({ data: [] })),
      ]);
      setAudiences(audRes.data.audiences || []);
      setCrmAudiences(crmRes.data || []);
    } catch (e) {
      setAudiences([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setMode('list'); setSelectedCrmAudienceId(''); setEligibleCount(null);
      setCustomName(''); setLookalikeOriginId(''); setLookalikeName(''); setLookalikeRatio('5');
      loadAll();
    }
  }, [open, loadAll]);

  const checkEligible = async (audienceId) => {
    setSelectedCrmAudienceId(audienceId);
    setEligibleCount(null);
    if (!audienceId) return;
    setCheckingEligible(true);
    try {
      const { data } = await api.get(`/tenant/meta-ads/audiences/${audienceId}/eligible-count`);
      setEligibleCount(data.eligible_count ?? 0);
    } catch (e) {
      setEligibleCount(0);
    } finally {
      setCheckingEligible(false);
    }
  };

  const createCustom = async () => {
    if (!selectedCrmAudienceId) { toast.error('Elige un segmento de tu CRM'); return; }
    if (!customName.trim()) { toast.error('Ponle un nombre al público'); return; }
    setCreating(true);
    try {
      await api.post('/tenant/meta-ads/audiences/custom', {
        marketing_audience_id: selectedCrmAudienceId, name: customName.trim(),
      });
      toast.success('Público personalizado creado en Meta — puede tardar hasta 24h en estar listo para usarse');
      setMode('list');
      await loadAll();
    } catch (e) {
      const code = e.response?.data?.detail?.code;
      if (code === 'no_eligible_contacts') {
        toast.error('Ninguno de los contactos de este segmento tiene consentimiento registrado — no se puede subir a Meta');
      } else {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    } finally {
      setCreating(false);
    }
  };

  const createLookalike = async () => {
    if (!lookalikeOriginId) { toast.error('Elige el público personalizado de origen'); return; }
    if (!lookalikeName.trim()) { toast.error('Ponle un nombre al público similar'); return; }
    setCreating(true);
    try {
      await api.post('/tenant/meta-ads/audiences/lookalike', {
        name: lookalikeName.trim(), origin_audience_id: lookalikeOriginId,
        ratio: (parseFloat(lookalikeRatio) || 5) / 100,
      });
      toast.success('Público similar creado en Meta — puede tardar hasta 24h en estar listo para usarse');
      setMode('list');
      await loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const customAudiences = audiences.filter(a => a.subtype !== 'LOOKALIKE');

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Users className="w-4 h-4" /> Públicos de Meta Ads</DialogTitle></DialogHeader>
        {mode === 'list' && (
          <div className="space-y-4 py-2">
            <p className="text-xs text-[#64748B]">Un "público" es a quién le muestras tus anuncios más allá de edad/país/intereses: tus propios clientes (personalizado) o gente parecida a ellos (similar/lookalike).</p>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setMode('new_custom')} className="gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Público desde mi CRM</Button>
              <Button size="sm" variant="outline" onClick={() => setMode('new_lookalike')} disabled={customAudiences.length === 0} className="gap-1.5">
                Público similar
              </Button>
            </div>
            {loading ? (
              <p className="text-xs text-[#94A3B8]">Cargando…</p>
            ) : audiences.length === 0 ? (
              <p className="text-sm text-[#94A3B8] text-center py-6">Sin públicos todavía — crea el primero arriba</p>
            ) : (
              <div className="bg-white border border-[#E2E8F0] rounded-xl divide-y divide-[#F1F5F9]">
                {audiences.map(a => (
                  <div key={a.id} className="px-3 py-2 flex items-center justify-between text-sm">
                    <div>
                      <p className="text-[#0F172A]">{a.name}</p>
                      <p className="text-[11px] text-[#94A3B8]">
                        {a.subtype === 'LOOKALIKE' ? 'Similar' : 'Personalizado'} · {a.approximate_count_lower_bound ?? '—'}{a.approximate_count_upper_bound ? `–${a.approximate_count_upper_bound}` : ''} personas
                      </p>
                    </div>
                    <Badge className={a.operation_status?.code === 'normal' ? 'bg-emerald-50 text-emerald-600' : 'bg-[#F1F5F9] text-[#64748B]'}>
                      {a.operation_status?.code === 'normal' ? 'Listo' : 'Procesando'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {mode === 'new_custom' && (
          <div className="space-y-3 py-2">
            <p className="text-xs text-[#64748B]">Sube (de forma cifrada) los contactos de un segmento de tu CRM a Meta para mostrarles anuncios. <strong>Solo se suben contactos que ya te dieron consentimiento</strong> — nunca toda tu base de golpe.</p>
            <div>
              <Label className="text-xs">Segmento de CRM</Label>
              <Select value={selectedCrmAudienceId} onValueChange={checkEligible}>
                <SelectTrigger><SelectValue placeholder="Elige un segmento" /></SelectTrigger>
                <SelectContent>
                  {crmAudiences.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {crmAudiences.length === 0 && <p className="text-[11px] text-amber-600 mt-1">Aún no tienes segmentos de CRM creados — créalos primero en Audiencias.</p>}
            </div>
            {checkingEligible && <p className="text-xs text-[#94A3B8]">Revisando cuántos contactos tienen consentimiento…</p>}
            {eligibleCount !== null && !checkingEligible && (
              <p className={`text-xs rounded-lg p-2 border ${eligibleCount > 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                {eligibleCount > 0
                  ? `${eligibleCount} contacto(s) con consentimiento listos para subirse.`
                  : 'Ningún contacto de este segmento tiene consentimiento registrado todavía — no hay nada que subir.'}
              </p>
            )}
            <div>
              <Label className="text-xs">Nombre del público en Meta</Label>
              <Input value={customName} onChange={e => setCustomName(e.target.value)} placeholder="Ej. Clientes activos 2026" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => setMode('list')}>Volver</Button>
              <Button size="sm" onClick={createCustom} disabled={creating || !eligibleCount} className="gap-1.5">
                {creating && <Loader2 className="w-4 h-4 animate-spin" />} Crear público
              </Button>
            </div>
          </div>
        )}
        {mode === 'new_lookalike' && (
          <div className="space-y-3 py-2">
            <p className="text-xs text-[#64748B]">Meta busca gente parecida a un público personalizado que ya tengas. Entre más chico el porcentaje, más parecidos (pero menos alcance); entre más grande, más alcance (pero menos parecidos).</p>
            <div>
              <Label className="text-xs">Público de origen</Label>
              <Select value={lookalikeOriginId} onValueChange={setLookalikeOriginId}>
                <SelectTrigger><SelectValue placeholder="Elige un público personalizado" /></SelectTrigger>
                <SelectContent>
                  {customAudiences.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Nombre del público similar</Label>
              <Input value={lookalikeName} onChange={e => setLookalikeName(e.target.value)} placeholder="Ej. Similar a clientes activos" />
            </div>
            <div>
              <Label className="text-xs">Qué tan parecidos (1% a 20%)</Label>
              <Input type="number" min="1" max="20" value={lookalikeRatio} onChange={e => setLookalikeRatio(e.target.value)} />
              <p className="text-[11px] text-[#94A3B8] mt-1">1% = muy parecidos, poco alcance. 20% = menos parecidos, mucho alcance. La mayoría empieza en 5%.</p>
            </div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => setMode('list')}>Volver</Button>
              <Button size="sm" onClick={createLookalike} disabled={creating} className="gap-1.5">
                {creating && <Loader2 className="w-4 h-4 animate-spin" />} Crear público similar
              </Button>
            </div>
          </div>
        )}
        {mode === 'list' && <DialogFooter><Button variant="outline" onClick={onClose}>Cerrar</Button></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

function PixelModal({ open, onClose }) {
  const [pixels, setPixels] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newPixelName, setNewPixelName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tenant/meta-ads/pixels');
      setPixels(data.pixels || []);
      setSelectedId(data.selected_pixel_id || null);
    } catch (e) {
      setPixels([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) { setNewPixelName(''); load(); } }, [open, load]);

  const select = async (pixel) => {
    setSaving(true);
    try {
      await api.post('/tenant/meta-ads/pixels/select', { pixel_id: pixel.id, pixel_name: pixel.name });
      toast.success(`Píxel "${pixel.name}" conectado`);
      setSelectedId(pixel.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const createNew = async () => {
    if (!newPixelName.trim()) { toast.error('Ponle un nombre al píxel'); return; }
    setCreating(true);
    try {
      const { data } = await api.post('/tenant/meta-ads/pixels/create', { name: newPixelName.trim() });
      toast.success('Píxel creado y conectado');
      setNewPixelName('');
      setSelectedId(data.id);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const copyId = (id) => {
    navigator.clipboard?.writeText(id);
    toast.success('ID del píxel copiado');
  };

  const selectedPixel = pixels.find(p => p.id === selectedId);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Target className="w-4 h-4" /> Píxel de Meta</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-xs text-[#64748B]">
            El píxel es un código que instalas en tu sitio web para que Meta sepa qué pasó después de que alguien hizo clic en tu anuncio (compró, agendó, llenó un formulario). Conéctalo o crea uno aquí, y CavernicolApp guarda cuál usar.
          </p>
          {loading ? (
            <p className="text-xs text-[#94A3B8]">Cargando…</p>
          ) : (
            <>
              {selectedPixel && (
                <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-medium text-emerald-700 flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Conectado: {selectedPixel.name}</p>
                  <div className="flex items-center gap-2">
                    <code className="text-[11px] bg-white border border-emerald-200 rounded px-2 py-1 flex-1 truncate">{selectedPixel.id}</code>
                    <Button size="icon" variant="outline" className="w-7 h-7 shrink-0" onClick={() => copyId(selectedPixel.id)} title="Copiar ID">
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <p className="text-[11px] text-emerald-700">Pega este ID (o el código base del píxel, disponible en Meta Events Manager) en tu sitio web para que empiece a medir conversiones.</p>
                </div>
              )}
              {pixels.length > 0 && (
                <div>
                  <Label className="text-xs">Píxeles disponibles en tu cuenta publicitaria</Label>
                  <div className="border border-[#E2E8F0] rounded-lg divide-y divide-[#F1F5F9] mt-1">
                    {pixels.map(p => (
                      <div key={p.id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="text-[#0F172A]">{p.name}</span>
                        {p.connected ? (
                          <Badge className="bg-emerald-50 text-emerald-600">Conectado</Badge>
                        ) : (
                          <Button size="sm" variant="outline" disabled={saving} onClick={() => select(p)}>Usar este</Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="border-t border-[#F1F5F9] pt-3 space-y-2">
                <Label className="text-xs">O crea un píxel nuevo</Label>
                <div className="flex gap-2">
                  <Input value={newPixelName} onChange={e => setNewPixelName(e.target.value)} placeholder="Ej. Píxel sitio principal" />
                  <Button size="sm" onClick={createNew} disabled={creating} className="gap-1.5 shrink-0">
                    {creating && <Loader2 className="w-4 h-4 animate-spin" />} Crear
                  </Button>
                </div>
              </div>
              <p className="text-[11px] text-[#94A3B8] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2.5 flex items-start gap-1.5">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                Por ahora el píxel es solo para que instales el código en tu sitio y mida conversiones por tu cuenta — todavía no se usa para que Meta optimice automáticamente tus campañas de Tráfico/Interacción/Leads (esa función de Meta necesita un objetivo de "Conversiones" que esta herramienta no ofrece todavía).
              </p>
            </>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cerrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
